
import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { FaPhoneAlt } from "react-icons/fa";
import { FiRepeat } from "react-icons/fi";
import { MdPhoneCallback } from "react-icons/md";
import { IoBanSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

const Dashrigmo = ({ setView, user }) => {
  const [data, setData] = useState({
    totalCalls: 0,
    successfulTransfers: 0,
    callbackSuggested: 0,
    dncList: 0,
  });
  const getStartOfMonth = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      .toISOString()
      .split("T")[0];
  };

  const [fromDate, setFromDate] = useState(getStartOfMonth());
  const [toDate, setToDate] = useState(""); 

  
  const [search, setSearch] = useState("");
  const [rawData, setRawData] = useState({
    calls: [],
    callbacks: [],
    dnc: [],
    positiveCalls: [], // Will remain empty unless a new endpoint is used
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user]);

  useEffect(() => {
    filterData();
  }, [fromDate, toDate, rawData]);

  const fetchData = async () => {
    try {
      // Fetch call history
      const totalCallsResponse = await fetch(
        `${process.env.REACT_APP_SERVER_API}/api/call-history?userId=${user.id}&role=${user.role}`
      );
      const totalCallsData = await totalCallsResponse.json();

      // Fetch callback suggested
      const callbackResponse = await fetch(
        `${process.env.REACT_APP_SERVER_API}/api/waiting-list?userId=${user.id}&role=${user.role}`
      );
      const callbackData = await callbackResponse.json();

      // Fetch DNC list
      const dncResponse = await fetch(
        `${process.env.REACT_APP_SERVER_API}/api/dnd-list?userId=${user.id}&role=${user.role}`
      );
      const dncData = await dncResponse.json();

      // Fetch positive call count and raw data
      const positiveCallCountResponse = await fetch(
        `${process.env.REACT_APP_SERVER_API}/api/positive-calls/positivecall-count?userId=${user.id}&role=${user.role}`
      );
      const positiveCallCountData = await positiveCallCountResponse.json();
      console.log("Positive call count response:", positiveCallCountData);


      setRawData({
        calls: totalCallsData || [],
        callbacks: callbackData || [],
        dnc: dncData || [],
        positiveCalls: positiveCallCountData.success ? positiveCallCountData.data || [] : [], // Store raw positive call data
      });

      // Temporary: Set successfulTransfers directly since we can't filter it
      setData(prev => ({
        ...prev,
        successfulTransfers: positiveCallCountData.success ? positiveCallCountData.data?.positiveCallCount || 0 : 0,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
      setRawData({
        calls: [],
        callbacks: [],
        dnc: [],
        positiveCalls: [],
      });
      setData({
        totalCalls: 0,
        successfulTransfers: 0,
        callbackSuggested: 0,
        dncList: 0,
      });
    }
  };

  const filterData = () => {
    const from = new Date(fromDate);
    const today = new Date();
    // If `toDate` is empty, use the end of today; otherwise, use the end of the selected `toDate`
    const to = toDate
      ? new Date(new Date(toDate).setHours(23, 59, 59, 999))
      : new Date(today.setHours(23, 59, 59, 999));
    const filteredCalls = rawData.calls.filter((call) => {
      const callDate = new Date(call.createdAt);
      return callDate >= from && callDate <= to;
    });
    const totalCalls = filteredCalls.length || 0;

    const filteredCallbacks = rawData.callbacks.filter((item) => {
      const itemDate = new Date(item.createdAt);
      return itemDate >= from && itemDate <= to;
    });
    const callbackSuggested = filteredCallbacks.length || 0;

    const filteredDnc = rawData.dnc.filter((item) => {
      const itemDate = new Date(item.createdAt);
      return itemDate >= from && itemDate <= to;
    });
    const dncList = filteredDnc.length || 0;


// Filter positive calls based on date range
    const filteredPositiveCalls = rawData.positiveCalls.filter((call) => {
      const callDate = new Date(call.createdAt);
      return callDate >= from && callDate <= to;
    });
    const successfulTransfers = filteredPositiveCalls.length || 0;
    // Note: successfulTransfers is not filtered here because rawData.positiveCalls is empty
    setData({
      totalCalls,
      successfulTransfers,
      callbackSuggested,
      dncList,
    });
  };

  // Color palette for consistency
  const COLORS = {
    totalCalls: "#3B82F6", // Blue
    successfulTransfers: "#10B981", // Green
    callbackSuggested: "#F59E0B", // Amber
    dncList: "#EF4444", // Red
  };

  const dataPie = [
    { name: "Total Calls", value: data.totalCalls, color: COLORS.totalCalls },
    { name: "Successful Transfers", value: data.successfulTransfers, color: COLORS.successfulTransfers },
    { name: "Callback Suggested", value: data.callbackSuggested, color: COLORS.callbackSuggested },
    { name: "DNC List", value: data.dncList, color: COLORS.dncList },
  ].filter(item => item.value > 0);

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-sm font-semibold"
      >
        {percent > 0 ? `${(percent * 100).toFixed(1)}%` : ""}
      </text>
    );
  };

  const dataBar = [
    { category: "Total Calls", count: data.totalCalls, color: COLORS.totalCalls },
    { category: "Successful Transfers", count: data.successfulTransfers, color: COLORS.successfulTransfers },
    { category: "Callback Suggested", count: data.callbackSuggested, color: COLORS.callbackSuggested },
    { category: "DNC List", count: data.dncList, color: COLORS.dncList },
  ].filter(item => item.count > 0);

  const handleTotalCallsClick = () => setView("CallHistory");
  const handleDncCallsClick = () => setView("DoNotDisturb");
  const handleCallbackClick = () => setView("WaitingList");

  return (
    <div className="bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Outbound Calls Dashboard</h2>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4  mb-6">
          <div className="flex flex-col w-full sm:w-auto">
            <label className="text-gray-700 font-semibold mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full sm:w-48 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>
          <div className="flex flex-col w-full sm:w-auto">
            <label className="text-gray-700 font-semibold mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full sm:w-48 px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>

        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <FaPhoneAlt className="text-3xl" style={{ color: COLORS.totalCalls }} />, label: "Total Calls", value: data.totalCalls, onClick: handleTotalCallsClick },
            { icon: <FiRepeat className="text-3xl" style={{ color: COLORS.successfulTransfers }} />, label: "Successful Transfers (All Time)", value: data.successfulTransfers },
            { icon: <MdPhoneCallback className="text-3xl" style={{ color: COLORS.callbackSuggested }} />, label: "Callback Suggested", value: data.callbackSuggested, onClick: handleCallbackClick },
            { icon: <IoBanSharp className="text-3xl" style={{ color: COLORS.dncList }} />, label: "DNC List", value: data.dncList, onClick: handleDncCallsClick },
          ].map((item, index) => (
            <div
              key={index}
              className={`p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 ${item.onClick ? "cursor-pointer hover:bg-gray-50" : ""}`}
              onClick={item.onClick || null}
            >
              <div className="flex items-center space-x-4">
                {item.icon}
                <div>
                  <p className="text-sm font-medium text-gray-600">{item.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Call Performance Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={dataPie} dataKey="value" cx="50%" cy="50%" outerRadius={120} innerRadius={60} label={renderCustomLabel} labelLine={false}>
                  {dataPie.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value} Calls`, name]} contentStyle={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Call Performance Metrics</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dataBar}>
                <XAxis dataKey="category" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip formatter={(value, name) => [`${value} Calls`, name]} contentStyle={{ borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }} />
                <Legend verticalAlign="bottom" height={36} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {dataBar.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashrigmo;